import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SparklesIcon, ArrowUpIcon, CarIcon, CalendarIcon, PlaneIcon, ShieldCheckIcon, MapPinIcon, CheckCircle2Icon } from 'lucide-react';
import { useBusiness } from '../components/BusinessContext';
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
};
// Rental-specific quick prompts — NOT generic marketplace categories.
const SHOP_PROMPTS = [{
  text: 'Show me your available SUVs',
  icon: CarIcon
}, {
  text: 'What are your monthly rental rates?',
  icon: CalendarIcon
}, {
  text: 'Do you offer airport pickup & delivery?',
  icon: PlaneIcon
}, {
  text: 'I need a car with full insurance',
  icon: ShieldCheckIcon
}];
export function Shop() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    hub
  } = useBusiness();
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isCurrentShop = hub?.shopSlug === slug;
  const shopName = isCurrentShop ? hub?.name ?? 'Easylease' : slug || 'Shop';
  const shopLogo = isCurrentShop ? hub?.logo : null;
  const shopHandle = hub?.shopSlug ?? slug;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chat, isTyping]);
  const ask = (text: string) => {
    if (!text.trim()) return;
    setChat(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setChat(prev => [...prev, {
        role: 'ai',
        content: `Great choice! At ${shopName} we offer daily, weekly, and monthly car rentals across the UAE — from economy and sedans to premium SUVs and luxury vehicles, all with full insurance options, airport pickup, and free delivery in Dubai. Tell me your dates and budget and I'll pull together the best options for you.`
      }]);
    }, 1100);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    ask(query);
  };
  return <div className="app-fill-screen flex-1 flex flex-col min-h-screen h-screen bg-white relative overflow-hidden" data-id="element-4083" data-mp="2oy8t7eky7j6h">
      {/* Background ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-4084" data-mp="1w8jvba3jbz93">
        <div className="absolute top-[18%] left-1/2 -translate-x-1/2 w-[900px] h-[640px] bg-ai-purple/[0.06] blur-[140px] rounded-full" data-id="element-4085" data-mp="1w6gwc0kemvs8" />
        <div className="absolute top-[8%] left-[12%] w-[420px] h-[420px] bg-primary-indigo/[0.05] blur-[130px] rounded-full" data-id="element-4086" data-mp="2667ohvlhsyp5" />
        <div className="absolute bottom-[6%] right-[14%] w-[460px] h-[460px] bg-electric-cyan/[0.05] blur-[140px] rounded-full" data-id="element-4087" data-mp="1ifu5eukvya6p" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 lg:px-12 pb-8 relative z-10" data-id="element-4088" data-mp="1dg7q8flhyjv9">
        <div className="max-w-3xl mx-auto w-full h-full flex flex-col" data-id="element-4089" data-mp="3p3azj0s2k2ae">
          {chat.length === 0 ?
        // Hero (mirrors Discover, tailored to the shop)
        <div className="flex-1 flex flex-col items-center pt-12 lg:pt-20 pb-12" data-id="element-4090" data-mp="3g60o9ff94d5h">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="w-full flex flex-col items-center relative z-10" data-id="element-4091" data-mp="3k5jbihujp0bh">
                {/* Shop logo with rotating ring */}
                <div className="relative mb-8" data-id="element-4092" data-mp="2nfikec6pe7r0">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 24,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute -inset-4 border border-slate-200/80 border-dashed rounded-full opacity-60" data-id="element-4093" data-mp="2neqtvfryr07l" />
                  <div className="absolute -inset-1 bg-gradient-to-br from-primary-indigo/20 to-ai-purple/20 rounded-full blur-md" data-id="element-4094" data-mp="2txdf6dpoqrkk" />
                  <div className="w-24 h-24 lg:w-28 lg:h-28 bg-white rounded-full flex items-center justify-center shadow-lg border border-slate-100 relative z-10 overflow-hidden" data-id="element-4095" data-mp="3qnw0zbkjjqau">
                    {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-4096" data-mp="3hlmser8u2703" /> : <span className="text-3xl font-bold text-primary-indigo" data-id="element-4097" data-mp="2693xnnzi8pz8">
                        {shopName.charAt(0)}
                      </span>}
                  </div>
                </div>

                {/* Heading */}
                <div className="text-center space-y-3 mb-8" data-id="element-4098" data-mp="lp8g1uokqwue">
                  {shopHandle && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.1
              }} className="inline-flex items-center gap-1.5 px-3 py-1 mb-2 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-500" data-id="element-4099" data-mp="pqtv9mokzn7r">
                      <CheckCircle2Icon className="w-3.5 h-3.5 text-success" data-id="element-4100" data-mp="3b6pg02pnzw5u" />
                      talab.app/{shopHandle}
                    </motion.div>}
                  <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-dark-navy tracking-tight leading-tight" data-id="element-4101" data-mp="3fadmsj6h2nq1">
                    Welcome to{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-4102" data-mp="hg1369r65xri">
                      {shopName}
                    </span>
                  </h1>
                  <p className="text-slate-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-4103" data-mp="rc47nm9hu8hm">
                    Your trusted UAE car rental partner — economy, sedans,
                    premium SUVs and luxury cars on daily, weekly and monthly
                    plans.
                  </p>
                </div>

                {/* AI Input */}
                <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.4
            }} className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_12px_40px_rgb(0,0,0,0.08)] border border-slate-100 p-2.5 mb-8 relative group transition-all hover:shadow-[0_16px_50px_rgb(0,0,0,0.12)] z-10" data-id="element-4104" data-mp="1a3n41a90ioao">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-4105" data-mp="1xq8ytwk5syl3" />
                  <form onSubmit={handleSubmit} className="relative bg-white rounded-[24px] p-5 lg:p-6 flex flex-col gap-4" data-id="element-4106" data-mp="1opu3039kk13c">
                    <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} placeholder={`Ask ${shopName} about our cars, rates or delivery…`} className="w-full resize-none outline-none text-lg lg:text-xl text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[52px]" rows={1} data-id="element-4107" data-mp="1f0ahsqydvv5b" />
                    <div className="flex items-center justify-between pt-2 border-t border-slate-50" data-id="element-4108" data-mp="12ihuytm2d95u">
                      <span className="text-xs font-medium text-slate-400 flex items-center gap-1.5" data-id="element-4109" data-mp="xrjadhu1lows">
                        <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-4110" data-mp="ssf3wyyi65a5" />
                        Powered by {shopName} AI
                      </span>
                      <button type="submit" disabled={!query.trim()} className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-md hover:shadow-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all" aria-label="Send" data-id="element-4111" data-mp="nvoma771v1mm">
                        <ArrowUpIcon className="w-5 h-5" data-id="element-4112" data-mp="2zgp7wezxz307" />
                      </button>
                    </div>
                  </form>
                </motion.div>

                {/* Tailored prompts */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.3
            }} className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12" data-id="element-4113" data-mp="27qu63iuwdkun">
                  {SHOP_PROMPTS.map(p => {
                const Icon = p.icon;
                return <button key={p.text} onClick={() => ask(p.text)} className="group flex items-center gap-3.5 px-5 py-4 bg-white/60 backdrop-blur-sm border border-slate-200/80 rounded-2xl text-left text-sm font-medium text-slate-700 shadow-sm hover:bg-white hover:border-primary-indigo/30 hover:shadow-md transition-all" data-id="element-4114" data-mp="1t03cq6dnqdpf">
                        <div className="w-8 h-8 rounded-full bg-primary-indigo/5 flex items-center justify-center group-hover:bg-primary-indigo/10 transition-colors flex-shrink-0" data-id="element-4115" data-mp="39vyt6tvm64li">
                          <Icon className="w-4 h-4 text-primary-indigo" data-id="element-4116" data-mp="2h8uut7r10oip" />
                        </div>
                        <span className="truncate group-hover:text-primary-indigo transition-colors" data-id="element-4117" data-mp="2m64h4ej4ck2b">
                          {p.text}
                        </span>
                      </button>;
              })}
                </motion.div>

                {/* Trust / Credential Row */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="flex flex-wrap justify-center gap-4 lg:gap-8 mt-auto pt-8 border-t border-slate-100/60 w-full max-w-4xl" data-id="element-4118" data-mp="1yfqzzj22o5mi">
                  {[{
                icon: ShieldCheckIcon,
                text: 'Fully Insured'
              }, {
                icon: PlaneIcon,
                text: 'Airport Pickup'
              }, {
                icon: MapPinIcon,
                text: 'Free Dubai Delivery'
              }, {
                icon: CarIcon,
                text: '500+ Vehicles'
              }].map((trust, i) => <div key={i} className="flex items-center gap-2 text-sm font-medium text-slate-500" data-id="element-4119" data-mp="9n7dottl4nfm" data-mpi={i}>
                      <trust.icon className="w-4 h-4 text-slate-400" data-id="element-4120" data-mp="nu1hte2tkr7" />
                      {trust.text}
                    </div>)}
                </motion.div>
              </motion.div>
            </div> :
        // Chat thread
        <div className="flex-1 py-8 space-y-5" data-id="element-4121" data-mp="td18zhvidkol">
              <button onClick={() => setChat([])} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors mb-2" data-id="element-4122" data-mp="xe30fcj6osg0">
                <SparklesIcon className="w-4 h-4" data-id="element-4123" data-mp="1cabyiarc8zqu" /> New conversation
              </button>

              {chat.map((m, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-4124" data-mp="1h3x16g3vmhwd" data-mpi={i}>
                  {m.role === 'ai' && <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden" data-id="element-4125" data-mp="17hrshh9b5g43">
                      {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-4126" data-mp="1hfo1o3ioq0zk" /> : <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-4127" data-mp="2su1l8w6enu9s" />}
                    </div>}
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary-indigo text-white rounded-br-md' : 'bg-white border border-slate-100 text-slate-700 shadow-sm rounded-bl-md'}`} data-id="element-4128" data-mp="2nydwuizqz55c">
                    {m.content}
                  </div>
                </motion.div>)}

              {isTyping && <div className="flex justify-start" data-id="element-4129" data-mp="1gds5za1nu0on">
                  <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 overflow-hidden" data-id="element-4130" data-mp="1gd8uab2ls8j2">
                    {shopLogo ? <img src={shopLogo} alt={shopName} className="w-full h-full object-cover" data-id="element-4131" data-mp="1q4wg9wge4xd5" /> : <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-4132" data-mp="1z62z4c6iik9k" />}
                  </div>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1" data-id="element-4133" data-mp="2410ahpr9l5pm">
                    {[0, 1, 2].map(d => <motion.span key={d} className="w-1.5 h-1.5 bg-slate-300 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: d * 0.2
              }} data-id="element-4134" data-mp="3joezabk1vwb1" />)}
                  </div>
                </div>}
              <div ref={bottomRef} data-id="element-4135" data-mp="3tl9t4h5dp8iz" />
            </div>}
        </div>
      </div>

      {/* Persistent input when in a conversation */}
      {chat.length > 0 && <div className="flex-shrink-0 px-4 lg:px-12 pb-6 pt-2 relative z-20 bg-gradient-to-t from-white via-white to-transparent" data-id="element-4136" data-mp="q3rkw8lp1zi1">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 flex items-end gap-2" data-id="element-4137" data-mp="1911jj9dtwy4g">
            <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }} placeholder={`Message ${shopName}…`} className="flex-1 resize-none outline-none text-base text-slate-800 placeholder:text-slate-300 bg-transparent px-3 py-2.5 min-h-[44px]" rows={1} data-id="element-4138" data-mp="1076q2ym2kw4n" />
            <button type="submit" disabled={!query.trim()} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0" aria-label="Send" data-id="element-4139" data-mp="2bmvc9nri1r4e">
              <ArrowUpIcon className="w-5 h-5" data-id="element-4140" data-mp="3w32w25pycbop" />
            </button>
          </form>
        </div>}

      {/* Powered by Talab */}
      <footer className="flex-shrink-0 relative z-20 py-4 text-center" data-id="element-4141" data-mp="8zogc81v9l50">
        <a href="https://talab.app" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-primary-indigo transition-colors" data-id="element-4142" data-mp="np2ojctphwit">
          <SparklesIcon className="w-3.5 h-3.5" data-id="element-4143" data-mp="is3vyypoxxmq" />
          Powered by{' '}
          <span className="font-semibold text-slate-500" data-id="element-4144" data-mp="wohabyyxhiwz">Talab.app</span>
        </a>
      </footer>
    </div>;
}